import { useId } from 'react'
import {
  RISK_LEVELS,
  RISK_LEVEL_LABELS,
  deriveRating,
} from '@deepguide-ai/types'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@deepguide-ai/ui/tooltip'

type MatrixCount = {
  likelihood: string | null
  impact: string | null
  count: number
}

const SEVERITY_BG: Record<string, string> = {
  CRITICAL: 'bg-status-red/70',
  HIGH: 'bg-status-orange-emphasis/70',
  MEDIUM: 'bg-status-orange/45',
  LOW: 'bg-status-green/40',
}

const SEVERITY_LABEL: Record<string, string> = {
  CRITICAL: 'Critical',
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
}

export default function RiskMatrixCard({
  counts,
  title = 'Risk Matrix',
  itemLabel = 'risks',
  marker,
  markerName = 'inherent risk',
  residualMarker,
  reviewed = false,
  treatmentLabel,
  scoringVersion = 2,
}: {
  scoringVersion?: number
  markerName?: string
  marker?: { likelihood: string | null; impact: string | null }
  residualMarker?: { likelihood: string | null; impact: string | null }
  reviewed?: boolean
  treatmentLabel?: string
  counts: MatrixCount[]
  title?: string
  itemLabel?: string
}) {
  const arrowId = useId()
  const IMPACT_LEVELS =
    scoringVersion === 1 ? (['LOW', 'MEDIUM', 'HIGH'] as const) : RISK_LEVELS
  const LIKELIHOOD_LEVELS = [...IMPACT_LEVELS].reverse()
  const label = (level: string) =>
    RISK_LEVEL_LABELS[level as keyof typeof RISK_LEVEL_LABELS] ?? level
  const cellSeverity = (likelihood: string, impact: string) =>
    deriveRating(likelihood, impact, scoringVersion)
  const columnStep = 270 / IMPACT_LEVELS.length
  const rowStep = 204 / IMPACT_LEVELS.length
  const centerX = 65 + columnStep / 2
  const centerY = 16 + rowStep / 2
  const columns =
    scoringVersion === 1
      ? 'grid-cols-[54px_repeat(3,minmax(0,1fr))]'
      : 'grid-cols-[54px_repeat(5,minmax(0,1fr))]'

  const grid = (() => {
    const map: Record<string, number> = {}
    for (const l of LIKELIHOOD_LEVELS) {
      for (const i of IMPACT_LEVELS) {
        map[`${l}-${i}`] = 0
      }
    }
    for (const row of counts) {
      const l = row.likelihood?.toUpperCase()
      const i = row.impact?.toUpperCase()
      const key = `${l}-${i}`
      if (key in map) map[key] += row.count
    }
    return map
  })()

  if (marker) {
    const row = LIKELIHOOD_LEVELS.findIndex(
      (level) => level === marker.likelihood
    )
    const column = IMPACT_LEVELS.findIndex((level) => level === marker.impact)
    const residualRow = LIKELIHOOD_LEVELS.findIndex(
      (level) => level === residualMarker?.likelihood
    )
    const residualColumn = IMPACT_LEVELS.findIndex(
      (level) => level === residualMarker?.impact
    )
    const hasResidual = residualRow >= 0 && residualColumn >= 0
    const sameCell =
      hasResidual && row === residualRow && column === residualColumn
    const ix = centerX + column * columnStep
    const iy = centerY + row * rowStep
    const rx = centerX + residualColumn * columnStep
    const ry = centerY + residualRow * rowStep
    const distance = Math.hypot(rx - ix, ry - iy)
    return (
      <div className='mt-7'>
        {title && <h3 className='text-sm font-medium'>{title}</h3>}
        <svg
          viewBox='0 0 350 280'
          role='img'
          aria-label={`Risk matrix: ${markerName}, ${label(marker.likelihood ?? 'unset')} likelihood, ${label(marker.impact ?? 'unset')} impact${hasResidual ? `; ${reviewed ? 'reviewed' : 'target'} residual risk, ${label(residualMarker?.likelihood ?? 'unset')} likelihood, ${label(residualMarker?.impact ?? 'unset')} impact` : ''}`}
          className='mt-4 w-full'
        >
          {LIKELIHOOD_LEVELS.map((likelihood, y) => (
            <g key={likelihood}>
              <text
                x='55'
                y={centerY + 4 + y * rowStep}
                textAnchor='end'
                className='fill-muted-foreground'
                fontSize='10'
              >
                {label(likelihood)}
              </text>
              {IMPACT_LEVELS.map((impact, x) => (
                <rect
                  key={impact}
                  x={65 + x * columnStep}
                  y={16 + y * rowStep}
                  width={columnStep}
                  height={rowStep}
                  rx='0'
                  className={
                    {
                      CRITICAL: 'fill-status-red/70',
                      HIGH: 'fill-status-orange-emphasis/70',
                      MEDIUM: 'fill-status-orange/45',
                      LOW: 'fill-status-green/40',
                    }[cellSeverity(likelihood, impact)]
                  }
                />
              ))}
            </g>
          ))}
          {hasResidual && row >= 0 && column >= 0 && !sameCell && (
            <g>
              <defs>
                <marker
                  id={arrowId}
                  markerWidth='8'
                  markerHeight='8'
                  refX='6'
                  refY='4'
                  orient='auto'
                >
                  <path
                    d='M0,0 L8,4 L0,8Z'
                    fill='#273a4a'
                    className={
                      reviewed
                        ? 'dark:fill-foreground'
                        : 'dark:fill-muted-foreground'
                    }
                  />
                </marker>
              </defs>
              <line
                x1={ix}
                y1={iy}
                x2={rx - ((rx - ix) / distance) * 16}
                y2={ry - ((ry - iy) / distance) * 16}
                stroke='#273a4a'
                className={
                  reviewed
                    ? 'dark:stroke-foreground'
                    : 'dark:stroke-muted-foreground'
                }
                strokeWidth='3.2'
                strokeDasharray={reviewed ? undefined : '5 3'}
                markerEnd={`url(#${arrowId})`}
              />
              {treatmentLabel && (
                <>
                  <rect
                    x={(ix + rx) / 2 - 29}
                    y={(iy + ry) / 2 - 10}
                    width='58'
                    height='18'
                    rx='9'
                    fill='white'
                    className='dark:fill-card'
                  />
                  <text
                    x={(ix + rx) / 2}
                    y={(iy + ry) / 2 + 2}
                    textAnchor='middle'
                    fontSize='9'
                    fill='#182532'
                    className={
                      reviewed
                        ? 'dark:fill-foreground'
                        : 'dark:fill-muted-foreground'
                    }
                  >
                    {treatmentLabel}
                  </text>
                </>
              )}
            </g>
          )}
          {hasResidual && (
            <g transform={`translate(${rx + (sameCell ? 13 : 0)},${ry})`}>
              <circle
                r='10'
                fill='#077e70'
                stroke='white'
                strokeWidth='3'
                className={
                  reviewed
                    ? 'dark:fill-foreground dark:stroke-card'
                    : 'dark:fill-muted-foreground dark:stroke-card'
                }
              />
              <text
                textAnchor='middle'
                y='3'
                fill='white'
                fontSize='9'
                fontWeight='700'
                className={reviewed ? 'dark:fill-background' : undefined}
              >
                R
              </text>
            </g>
          )}
          {row >= 0 && column >= 0 && (
            <g transform={`translate(${ix - (sameCell ? 13 : 0)},${iy})`}>
              <circle
                r='10'
                fill='#b83e20'
                stroke='white'
                strokeWidth='3'
                className='dark:fill-status-red dark:stroke-card'
              />
              <text
                textAnchor='middle'
                y='3'
                fill='white'
                fontSize='9'
                fontWeight='700'
              >
                {markerName.charAt(0).toUpperCase()}
              </text>
            </g>
          )}
          {IMPACT_LEVELS.map((level, x) => (
            <text
              key={level}
              x={centerX + x * columnStep}
              y='240'
              textAnchor='middle'
              className='fill-muted-foreground'
              fontSize='10'
            >
              {label(level)}
            </text>
          ))}
          <text
            x='200'
            y='265'
            textAnchor='middle'
            className='fill-muted-foreground'
            fontSize='10'
          >
            Impact
          </text>
          <text
            transform='translate(15,115) rotate(-90)'
            textAnchor='middle'
            className='fill-muted-foreground'
            fontSize='10'
          >
            Likelihood
          </text>
        </svg>
      </div>
    )
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className='flex flex-col px-5 py-4'>
        <div className='font-body text-text-subtle text-[10px] font-semibold tracking-[1px] uppercase'>
          {title}
        </div>

        <div className='mt-3 flex flex-1 items-center'>
          <div className='flex w-full'>
            {/* Y-axis label */}
            <div className='flex w-5 shrink-0 items-center justify-center'>
              <span className='font-body text-text-disabled -rotate-90 text-[8px] font-semibold tracking-[1px] whitespace-nowrap uppercase'>
                Likelihood
              </span>
            </div>

            <div className='flex-1'>
              {/* Column headers */}
              <div className={`mb-1 grid ${columns}`}>
                <div />
                {IMPACT_LEVELS.map((level) => (
                  <div
                    key={level}
                    className='font-body text-text-placeholder text-center text-[9px] leading-tight font-medium'
                  >
                    {label(level)}
                  </div>
                ))}
              </div>

              {/* Matrix rows */}
              <div className='grid'>
                {LIKELIHOOD_LEVELS.map((likelihood) => (
                  <div key={likelihood} className={`grid ${columns}`}>
                    <div className='font-body text-text-placeholder flex items-center justify-end pr-1 text-[9px] leading-tight font-medium'>
                      {label(likelihood)}
                    </div>
                    {IMPACT_LEVELS.map((impact) => {
                      const key = `${likelihood}-${impact}`
                      const count = grid[key]
                      const severity = cellSeverity(likelihood, impact)

                      return (
                        <Tooltip key={key}>
                          <TooltipTrigger asChild>
                            <div
                              aria-label={`${label(likelihood)} likelihood, ${label(impact)} impact: ${count} ${itemLabel}, ${SEVERITY_LABEL[severity]}`}
                              className={`flex h-6 items-center justify-center ${SEVERITY_BG[severity]}`}
                            >
                              <span
                                className={`font-heading text-[12px] font-bold ${count === 0 ? 'text-text-secondary/70' : 'text-text-secondary'}`}
                              >
                                {count}
                              </span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side='top' className='text-[11px]'>
                            <span className='font-medium'>
                              {SEVERITY_LABEL[severity]}
                            </span>
                            <span className='text-text-subtle'>
                              {' '}
                              · {label(likelihood)} likelihood, {label(impact)}{' '}
                              impact
                            </span>
                          </TooltipContent>
                        </Tooltip>
                      )
                    })}
                  </div>
                ))}
              </div>

              {/* X-axis label */}
              <div className='mt-1.5 grid grid-cols-[54px_1fr]'>
                <div />
                <div className='font-body text-text-disabled text-center text-[8px] font-semibold tracking-[1px] uppercase'>
                  Impact
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  )
}
